import { useEffect, useState } from 'react'
import { useRouter } from '@tanstack/react-router'

// Show after this delay, and only from the second viewed page onwards.
const SHOW_DELAY_MS = 20_000
const MIN_PAGEVIEWS = 2
// Once dismissed or submitted, do not show again for this long.
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000

const PAGEVIEWS_KEY = 'mv_newsletter_pageviews'
const DISMISSED_UNTIL_KEY = 'mv_newsletter_dismissed_until'

function isDismissed(): boolean {
  const until = Number(localStorage.getItem(DISMISSED_UNTIL_KEY) ?? '0')
  return Number.isFinite(until) && Date.now() < until
}

/**
 * Silence the popup for the dismissal window. Exported so other entry points to
 * the same subscription (the footer form) can stop the popup from nagging a
 * visitor who has already signed up.
 */
export function suppressNewsletterPopup(): void {
  localStorage.setItem(DISMISSED_UNTIL_KEY, String(Date.now() + DISMISS_DURATION_MS))
}

interface UseNewsletterPopup {
  open: boolean
  close: () => void
  markDismissed: () => void
}

export function useNewsletterPopup(): UseNewsletterPopup {
  const router = useRouter()
  const [open, setOpen] = useState(false)

  // Navigation and the delay timer are external events: the popup opens from
  // their callbacks, never synchronously from an effect body.
  useEffect(() => {
    let shown = false
    let timeReady = false

    const countPageview = () => {
      const pageviews = Number(sessionStorage.getItem(PAGEVIEWS_KEY) ?? '0') + 1
      sessionStorage.setItem(PAGEVIEWS_KEY, String(pageviews))
    }

    // Never interrupt the checkout flow (reservation -> confirmation -> order).
    const inCheckout = () =>
      router.state.matches.some(
        (match) =>
          match.routeId === '/_layout/ubytovani/$slug/$unitSlug/rezervace' ||
          match.routeId === '/_layout/ubytovani/$slug/$unitSlug/potvrzeni' ||
          match.routeId === '/_layout/objednavka/$orderNumber',
      )

    const maybeOpen = () => {
      if (shown || !timeReady || isDismissed() || inCheckout()) return

      const pageviews = Number(sessionStorage.getItem(PAGEVIEWS_KEY) ?? '0')
      if (pageviews < MIN_PAGEVIEWS) return

      shown = true
      setOpen(true)
    }

    // The page the app mounted on is a pageview of its own.
    countPageview()

    // Every later navigation to another path counts too and re-checks the
    // trigger. The first resolve (no previous location) is the page above.
    const unsubscribe = router.subscribe('onResolved', (event) => {
      if (!event.fromLocation || !event.pathChanged) return
      countPageview()
      maybeOpen()
    })

    // Arm the time-based trigger once.
    const timer = setTimeout(() => {
      timeReady = true
      maybeOpen()
    }, SHOW_DELAY_MS)

    return () => {
      unsubscribe()
      clearTimeout(timer)
    }
  }, [router])

  const close = () => {
    suppressNewsletterPopup()
    setOpen(false)
  }

  return { open, close, markDismissed: suppressNewsletterPopup }
}
